import { Link } from "react-router-dom";
import {
  Building2,
  Database,
  Fingerprint,
  Target,
  Share2,
  Cookie,
  Lock,
  KeyRound,
  RotateCcw,
  Archive,
  Baby,
  RefreshCw,
  Mail,
  CheckCircle2,
  AlertTriangle,
} from "lucide-react";
import useSeo from "../hooks/useSeo";

const SECTIONS = [
  {
    id: "responsable",
    n: "1",
    icon: Building2,
    title: "Identidad y Domicilio del Responsable",
    body: (
      <>
        <p>El responsable del tratamiento de sus datos personales es:</p>
        <p>
          <strong>RivoNes</strong>
          <br />
          Operado por: <strong>Novus Core Technologies LLC</strong>
        </p>
        <p>
          Correo electrónico de contacto para privacidad y datos personales:{" "}
          <a
            href="mailto:privacy@rivones.com"
            className="text-terracotta font-semibold hover:underline"
          >
            privacy@rivones.com
          </a>
        </p>
        <p>
          Sitio web oficial:{" "}
          <Link to="/" className="text-terracotta font-semibold hover:underline">
            RivoNes
          </Link>
        </p>
      </>
    ),
  },
  {
    id: "datos",
    n: "2",
    icon: Database,
    title: "Datos Personales que Recabamos",
    body: (
      <>
        <p>RivoNes podrá recopilar las siguientes categorías de datos personales:</p>

        <h4>A. Datos de identificación</h4>
        <ul>
          <li>Nombre completo</li>
          <li>Fecha de nacimiento</li>
          <li>Nacionalidad</li>
          <li>Fotografía</li>
          <li>Firma</li>
          <li>CURP (si aplica)</li>
          <li>RFC (si aplica)</li>
        </ul>

        <h4>B. Datos de contacto</h4>
        <ul>
          <li>Correo electrónico</li>
          <li>Número telefónico</li>
          <li>Dirección física</li>
          <li>Ciudad y país de residencia</li>
        </ul>

        <h4>C. Datos de identificación oficial</h4>
        <ul>
          <li>INE/IFE</li>
          <li>Pasaporte</li>
          <li>Licencia de conducir</li>
          <li>Selfie o biometría facial para validación de identidad</li>
          <li>Comprobante de domicilio</li>
        </ul>

        <h4>D. Datos financieros y de pago</h4>
        <ul>
          <li>Tarjetas bancarias</li>
          <li>Información parcial de cuentas bancarias</li>
          <li>Historial de pagos</li>
          <li>Facturación</li>
          <li>Información relacionada con Stripe, Mercado Pago u otros procesadores autorizados</li>
        </ul>

        <h4>E. Datos vehiculares (Hosts)</h4>
        <ul>
          <li>Tarjeta de circulación</li>
          <li>Número VIN</li>
          <li>Placas</li>
          <li>Seguro vehicular</li>
          <li>Fotografías del vehículo</li>
          <li>Información mecánica y legal del automóvil</li>
        </ul>

        <h4>F. Datos tecnológicos y de navegación</h4>
        <ul>
          <li>Dirección IP</li>
          <li>Tipo de dispositivo</li>
          <li>Sistema operativo</li>
          <li>Ubicación aproximada</li>
          <li>Cookies</li>
          <li>Historial de navegación dentro de la plataforma</li>
        </ul>

        <h4>G. Datos de geolocalización</h4>
        <p>RivoNes podrá recopilar información de ubicación en tiempo real para:</p>
        <ul>
          <li>Verificar entregas y devoluciones</li>
          <li>Prevenir fraude</li>
          <li>Monitorear seguridad</li>
          <li>Optimizar experiencia del usuario</li>
        </ul>
      </>
    ),
  },
  {
    id: "sensibles",
    n: "3",
    icon: Fingerprint,
    title: "Datos Personales Sensibles",
    body: (
      <>
        <p>
          RivoNes podrá recopilar <strong>datos biométricos y de validación facial</strong>{" "}
          exclusivamente para procesos de:
        </p>
        <ul>
          <li>Verificación de identidad</li>
          <li>Prevención de fraude</li>
          <li>Seguridad de usuarios y vehículos</li>
        </ul>
        <p>
          Estos datos serán tratados bajo estrictas medidas de seguridad administrativas, físicas y
          tecnológicas.
        </p>
      </>
    ),
  },
  {
    id: "finalidades",
    n: "4",
    icon: Target,
    title: "Finalidades del Tratamiento de Datos",
    body: (
      <>
        <p>
          Sus datos personales serán utilizados para las siguientes finalidades primarias:
        </p>

        <h4>Finalidades necesarias</h4>
        <ul>
          <li>Crear y administrar cuentas de usuario</li>
          <li>Verificar identidad</li>
          <li>Validar licencias y documentación</li>
          <li>Procesar reservaciones y pagos</li>
          <li>Facilitar la renta de vehículos</li>
          <li>Gestionar seguros y reclamaciones</li>
          <li>Prevenir fraude y actividades ilícitas</li>
          <li>Dar soporte técnico y atención al cliente</li>
          <li>Cumplir obligaciones legales y regulatorias</li>
          <li>Emitir facturación</li>
          <li>Gestionar devoluciones y depósitos</li>
          <li>Mejorar la seguridad de la plataforma</li>
        </ul>

        <h4>Finalidades secundarias</h4>
        <ul>
          <li>Marketing y promociones</li>
          <li>Publicidad personalizada</li>
          <li>Estudios estadísticos</li>
          <li>Encuestas de satisfacción</li>
          <li>Programas de fidelidad</li>
          <li>Campañas de email y WhatsApp</li>
        </ul>
        <p>
          Usted podrá oponerse al tratamiento de sus datos para finalidades secundarias enviando un
          correo a:{" "}
          <a
            href="mailto:privacy@rivones.com"
            className="text-terracotta font-semibold hover:underline"
          >
            privacy@rivones.com
          </a>
        </p>
      </>
    ),
  },
  {
    id: "transferencia",
    n: "5",
    icon: Share2,
    title: "Transferencia de Datos Personales",
    body: (
      <>
        <p>RivoNes podrá compartir sus datos personales con:</p>

        <h4>A. Proveedores tecnológicos</h4>
        <ul>
          <li>Plataformas de procesamiento de pagos</li>
          <li>Servicios de verificación de identidad</li>
          <li>Hosting y almacenamiento en la nube</li>
          <li>Servicios de análisis y seguridad</li>
        </ul>

        <h4>B. Aseguradoras y asistencia vial</h4>
        <p>Con el fin de gestionar:</p>
        <ul>
          <li>Cobertura de seguros</li>
          <li>Accidentes</li>
          <li>Reclamaciones</li>
          <li>Asistencia en carretera</li>
        </ul>

        <h4>C. Autoridades gubernamentales</h4>
        <p>Cuando sea requerido legalmente por:</p>
        <ul>
          <li>Autoridades judiciales</li>
          <li>Autoridades fiscales</li>
          <li>Autoridades de seguridad pública</li>
        </ul>

        <h4>D. Socios comerciales autorizados</h4>
        <p>
          Exclusivamente para la operación de la plataforma y prestación de servicios relacionados.
        </p>
        <div className="mt-5 p-5 rounded-2xl border border-emerald-200 bg-emerald-50 not-prose">
          <p className="text-sm text-emerald-900 font-semibold flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4" />
            Compromiso
          </p>
          <p className="text-sm text-emerald-900 mt-1">
            En ningún caso RivoNes venderá sus datos personales a terceros no autorizados.
          </p>
        </div>
      </>
    ),
  },
  {
    id: "cookies",
    n: "6",
    icon: Cookie,
    title: "Uso de Cookies y Tecnologías de Rastreo",
    body: (
      <>
        <p>
          RivoNes utiliza cookies, web beacons y tecnologías similares para:
        </p>
        <ul>
          <li>Mejorar experiencia del usuario</li>
          <li>Recordar preferencias</li>
          <li>Analizar tráfico y comportamiento</li>
          <li>Seguridad y prevención de fraude</li>
          <li>Publicidad y remarketing</li>
        </ul>
        <p>El usuario puede modificar la configuración de cookies desde su navegador.</p>
      </>
    ),
  },
  {
    id: "seguridad",
    n: "7",
    icon: Lock,
    title: "Medidas de Seguridad",
    body: (
      <>
        <p>
          RivoNes implementa medidas de seguridad administrativas, técnicas y físicas para proteger
          los datos personales contra:
        </p>
        <ul>
          <li>Daño</li>
          <li>Pérdida</li>
          <li>Alteración</li>
          <li>Destrucción</li>
          <li>Acceso no autorizado</li>
        </ul>
        <p>Entre ellas:</p>
        <ul>
          <li>Cifrado SSL</li>
          <li>Acceso restringido</li>
          <li>Monitoreo de seguridad</li>
          <li>Verificación multifactor</li>
          <li>Almacenamiento seguro</li>
        </ul>
      </>
    ),
  },
  {
    id: "arco",
    n: "8",
    icon: KeyRound,
    title: "Derechos ARCO",
    body: (
      <>
        <p>Usted tiene derecho a:</p>
        <ul>
          <li>
            <strong>Acceder</strong> a sus datos personales
          </li>
          <li>
            <strong>Rectificar</strong> datos incorrectos
          </li>
          <li>
            <strong>Cancelar</strong> sus datos cuando considere que no son necesarios
          </li>
          <li>
            <strong>Oponerse</strong> al tratamiento de los mismos
          </li>
        </ul>
        <p>
          Estos derechos son conocidos como <strong>Derechos ARCO</strong>.
        </p>

        <h4>Procedimiento para ejercer Derechos ARCO</h4>
        <p>Debe enviar una solicitud al correo:</p>
        <p>
          <a
            href="mailto:privacy@rivones.com"
            className="text-terracotta font-semibold hover:underline"
          >
            privacy@rivones.com
          </a>
        </p>
        <p>Incluyendo:</p>
        <ul>
          <li>Nombre completo</li>
          <li>Identificación oficial</li>
          <li>Derecho que desea ejercer</li>
          <li>Descripción clara de la solicitud</li>
        </ul>
        <p>RivoNes responderá dentro de los plazos establecidos por la LFPDPPP.</p>
      </>
    ),
  },
  {
    id: "revocacion",
    n: "9",
    icon: RotateCcw,
    title: "Revocación del Consentimiento",
    body: (
      <p>
        El usuario podrá revocar en cualquier momento el consentimiento otorgado para el
        tratamiento de sus datos personales, salvo en los casos donde exista obligación legal de
        conservarlos.
      </p>
    ),
  },
  {
    id: "conservacion",
    n: "10",
    icon: Archive,
    title: "Conservación de Datos",
    body: (
      <>
        <p>
          RivoNes conservará los datos personales únicamente durante el tiempo necesario para:
        </p>
        <ul>
          <li>Cumplir obligaciones legales</li>
          <li>Resolver disputas</li>
          <li>Prevenir fraude</li>
          <li>Hacer cumplir contratos y políticas</li>
        </ul>
      </>
    ),
  },
  {
    id: "menores",
    n: "11",
    icon: Baby,
    title: "Menores de Edad",
    body: (
      <>
        <p>
          Los servicios de RivoNes están dirigidos exclusivamente a personas{" "}
          <strong>mayores de 18 años</strong> con capacidad legal para contratar.
        </p>
        <div className="mt-5 p-5 rounded-2xl border border-amber-200 bg-amber-50 not-prose">
          <p className="text-sm text-amber-900 font-semibold flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" />
            Importante
          </p>
          <p className="text-sm text-amber-900 mt-1">
            RivoNes no recopila intencionalmente datos personales de menores de edad.
          </p>
        </div>
      </>
    ),
  },
  {
    id: "cambios",
    n: "12",
    icon: RefreshCw,
    title: "Cambios al Aviso de Privacidad",
    body: (
      <>
        <p>
          RivoNes podrá modificar este Aviso de Privacidad en cualquier momento para cumplir cambios
          legales, regulatorios u operativos.
        </p>
        <p>Las modificaciones serán publicadas en:</p>
        <p>
          <Link to="/" className="text-terracotta font-semibold hover:underline">
            RivoNes
          </Link>
        </p>
      </>
    ),
  },
  {
    id: "contacto",
    n: "13",
    icon: Mail,
    title: "Contacto",
    body: (
      <>
        <p>
          Para cualquier duda relacionada con privacidad y protección de datos personales:
        </p>
        <p>
          <strong>Departamento de Privacidad – RivoNes</strong>
        </p>
        <p>
          Correo electrónico:{" "}
          <a
            href="mailto:privacy@rivones.com"
            className="text-terracotta font-semibold hover:underline"
          >
            privacy@rivones.com
          </a>
        </p>
        <p>
          Sitio web:{" "}
          <Link to="/" className="text-terracotta font-semibold hover:underline">
            RivoNes
          </Link>
        </p>
      </>
    ),
  },
];

export default function AvisoPrivacidadPage() {
  useSeo({
    title: "Aviso de Privacidad Integral · Rivones",
    description:
      "Aviso de Privacidad Integral de RivoNes conforme a la LFPDPPP de México. Conoce cómo recabamos, usamos y protegemos tus datos personales.",
    keywords:
      "aviso de privacidad rivones, LFPDPPP, datos personales, derechos ARCO, privacidad renta de autos México",
    canonical: "https://rivones.com/privacidad",
  });

  return (
    <div className="bg-sand min-h-screen" data-testid="privacidad-page">
      {/* Hero */}
      <section className="bg-gradient-to-b from-neutral-50 to-sand border-b border-neutral-200">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-14 sm:py-20">
          <p className="text-sm font-semibold tracking-[0.2em] text-terracotta uppercase">
            Documento legal
          </p>
          <h1
            className="font-heading font-bold text-4xl sm:text-5xl lg:text-6xl text-[#111111] tracking-tight mt-3"
            data-testid="privacidad-h1"
          >
            Aviso de Privacidad Integral
          </h1>
          <p className="text-base sm:text-lg text-neutral-600 mt-4 leading-relaxed">
            Plataforma de Compartición y Renta de Vehículos. Conforme a la{" "}
            <strong>Ley Federal de Protección de Datos Personales en Posesión de los Particulares
            (LFPDPPP)</strong>{" "}
            — México.
          </p>
          <p className="text-xs uppercase tracking-[0.18em] text-neutral-400 mt-6 font-semibold">
            Última actualización · Mayo 2026
          </p>
        </div>
      </section>

      {/* Preamble */}
      <section className="max-w-3xl mx-auto px-4 sm:px-6 pt-12 sm:pt-16">
        <div className="space-y-4 text-neutral-700 leading-relaxed">
          <p>
            Este Aviso de Privacidad regula el tratamiento de los datos personales recabados por{" "}
            <strong>RivoNes</strong>, plataforma digital operada por{" "}
            <strong>Novus Core Technologies LLC</strong>, con domicilio para efectos legales en los
            Estados Unidos de América y operaciones comerciales en México.
          </p>
          <p>
            RivoNes reconoce la importancia de proteger la privacidad y los datos personales de sus
            usuarios, anfitriones (“hosts”), arrendatarios (“renters”), visitantes y cualquier
            persona que interactúe con la plataforma web, aplicación móvil, redes sociales o
            servicios relacionados.
          </p>
          <p>Este Aviso de Privacidad se emite en cumplimiento de la:</p>
          <ul className="list-disc pl-5 space-y-1">
            <li>Ley Federal de Protección de Datos Personales en Posesión de los Particulares (LFPDPPP)</li>
            <li>Reglamento de la LFPDPPP</li>
            <li>Lineamientos del Aviso de Privacidad emitidos por el INAI</li>
          </ul>
        </div>
      </section>

      {/* Body */}
      <section className="max-w-3xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
        {/* Tracking & analytics disclosure */}
        <div
          className="mb-10 p-5 rounded-2xl border border-blue-200 bg-blue-50/60"
          data-testid="privacidad-tracking-disclosure"
        >
          <p className="text-xs uppercase tracking-[0.18em] text-blue-700 font-semibold mb-2">
            Información técnica y de uso
          </p>
          <p className="text-sm text-blue-900 leading-relaxed">
            RivoNes may collect technical and usage information when users visit our website or use
            our app, including IP address, approximate geographic location such as city, state, and
            country, device type, browser, operating system, pages visited, access dates and times,
            referral source, and campaign tracking data. This information is used for analytics,
            marketing optimization, fraud prevention, platform security, service improvement, and
            business reporting.
          </p>
        </div>

        {/* TOC */}
        <nav
          aria-label="Índice"
          className="bg-white border border-neutral-200 rounded-2xl p-5 mb-10"
          data-testid="privacidad-toc"
        >
          <p className="text-xs uppercase tracking-[0.18em] text-neutral-500 font-semibold mb-3">
            Índice
          </p>
          <ol className="space-y-1.5 text-sm">
            {SECTIONS.map(({ id, n, title }) => (
              <li key={id}>
                <a
                  href={`#${id}`}
                  className="text-neutral-700 hover:text-terracotta transition-colors flex gap-2"
                >
                  <span className="text-neutral-400 tabular-nums">{n}.</span>
                  <span>{title}</span>
                </a>
              </li>
            ))}
          </ol>
        </nav>

        <div className="space-y-12">
          {SECTIONS.map(({ id, n, icon: Icon, title, body }) => (
            <article
              key={id}
              id={id}
              data-testid={`privacidad-section-${id}`}
              className="scroll-mt-24"
            >
              <div className="flex items-center gap-3 mb-4">
                <span className="inline-flex w-11 h-11 rounded-xl bg-neutral-100 items-center justify-center">
                  <Icon className="w-5 h-5 text-[#111111]" strokeWidth={2} />
                </span>
                <div>
                  <p className="text-xs uppercase tracking-[0.18em] text-neutral-400 font-semibold">
                    Sección {n}
                  </p>
                  <h2 className="font-heading font-bold text-2xl sm:text-3xl text-[#111111] leading-tight">
                    {title}
                  </h2>
                </div>
              </div>
              <div className="max-w-none text-neutral-700 leading-relaxed [&_p]:my-3 [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:my-3 [&_ul]:space-y-1 [&_li]:my-1 [&_strong]:text-[#111111] [&_strong]:font-semibold [&_h4]:font-semibold [&_h4]:text-base [&_h4]:mt-5 [&_h4]:mb-2 [&_h4]:text-[#111111]">
                {body}
              </div>
            </article>
          ))}
        </div>

        {/* Consentimiento */}
        <div
          className="mt-14 p-6 rounded-2xl bg-[#111111] text-white"
          data-testid="privacidad-consentimiento"
        >
          <p className="text-xs uppercase tracking-[0.2em] text-cyan-300/90 font-semibold mb-3">
            Consentimiento
          </p>
          <p className="text-base leading-relaxed text-white/90">
            Al utilizar la plataforma, crear una cuenta o proporcionar información personal a
            RivoNes, usted reconoce haber leído y aceptado el presente Aviso de Privacidad Integral
            conforme a la legislación mexicana aplicable.
          </p>
        </div>

        <div className="mt-8 p-5 rounded-2xl bg-neutral-100 border border-neutral-200 text-sm text-neutral-600">
          Consulta también nuestros{" "}
          <Link to="/terminos" className="text-terracotta font-semibold hover:underline">
            Términos y Condiciones
          </Link>{" "}
          y la{" "}
          <Link to="/elegibilidad" className="text-terracotta font-semibold hover:underline">
            política de Elegibilidad y Verificación
          </Link>
          .
        </div>
      </section>
    </div>
  );
}
